import {
  Directive,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  output,
  AfterViewInit,
  input,
  effect,
} from '@angular/core';
import {
  createTooltip,
  setTooltipPosition,
  watchTooltipAnchor,
  TTooltipAnchorWatcher,
  TTooltipConfig,
} from './tooltip';
import { windowOf } from '../anchor-document.util';

@Directive({
  selector: '[lib-truncate-tooltip]',
  standalone: true,
  exportAs: 'libTruncateTooltip',
})
export class TruncateTooltipDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly ngZone: NgZone = inject(NgZone);
  private tooltip: HTMLDivElement | null = null;
  public readonly disabled = input<boolean>(false);

  public removedTooltip = output<void>();
  private intersectionObserver: IntersectionObserver | null = null;
  private animationFrameId: number | null = null;
  private destroyed = false;
  private anchorWatcher: TTooltipAnchorWatcher | null = null;

  private readonly obseveToCreateTooltip = effect(() => {
    if (!this.disabled()) {
      this.addTooltip();
    } else {
      this.removeTooltip();
      this.removeListeners();
    }
  })

  public ngAfterViewInit(): void {
    this.addTooltip();
  }

  public addTooltip(): void {
    // Os listeners saem já e só voltam no próximo frame; um tooltip visível
    // ficaria sem `mouseleave` nesse intervalo.
    this.removeTooltip();
    this.removeListeners();
    // Cancela um setup já agendado: `addTooltip` é chamado pelo effect e pelo
    // `ngAfterViewInit`, e cada chamada substitui a anterior em vez de duplicar
    // listeners e IntersectionObservers.
    this.cancelScheduledSetup();
    if (this.destroyed) return;

    this.animationFrameId = this.scheduleFrame(() => {
      this.animationFrameId = null;
      if (this.destroyed || this.disabled() || !this.elementRef?.nativeElement?.textContent) return;

      this.observeVibility();
      this.elementRef.nativeElement.classList.add('truncate');
      this.elementRef.nativeElement.addEventListener('mouseenter', this.createTooltip, { passive: true });
      this.elementRef.nativeElement.addEventListener('mouseleave', this.removeTooltip, { passive: true });
      this.ngZone.runOutsideAngular(() => {
        this.elementRef.nativeElement.addEventListener('wheel', this.removeTooltip, { passive: true });
      });
    });
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.cancelScheduledSetup();
    this.removeTooltip();
    this.removeListeners();
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
  }

  /**
   * O Firefox lança `NS_ERROR_NOT_AVAILABLE` ao chamar `requestAnimationFrame`
   * num documento que já não está ativo (a sair da página, aba a ser
   * descartada, bfcache). Nesse cenário nenhum frame chega a correr, portanto
   * não há tooltip para montar e ignorar é o comportamento correto.
   */
  private scheduleFrame(callback: () => void): number | null {
    try {
      return this.anchorWindow.requestAnimationFrame(callback);
    } catch {
      return null;
    }
  }

  /**
   * Janela do documento onde o elemento está: no mini-monitor destacado
   * (Document Picture-in-Picture) é outra, e o `requestAnimationFrame` da
   * página principal para quando a aba fica em segundo plano.
   */
  private get anchorWindow(): Window & typeof globalThis {
    return windowOf(this.elementRef.nativeElement) ?? window;
  }

  private cancelScheduledSetup(): void {
    if (this.animationFrameId === null) return;
    this.anchorWindow.cancelAnimationFrame(this.animationFrameId);
    this.animationFrameId = null;
  }

  private removeListeners(): void {
    const element = this.elementRef?.nativeElement;
    if (!element) return;
    element.removeEventListener('mouseenter', this.createTooltip);
    element.removeEventListener('mouseleave', this.removeTooltip);
    element.removeEventListener('wheel', this.removeTooltip);
  }

  public readonly removeTooltip = (): void => {
    this.anchorWatcher?.stop();
    this.anchorWatcher = null;
    if (this.tooltip) {
      this.tooltip.remove();
      this.tooltip = null;
      this.removedTooltip.emit();
    }
  };

  private readonly createTooltip = (): void => {
    const isTruncated = this.elementRef.nativeElement.offsetWidth < this.elementRef.nativeElement.scrollWidth;
    if (!isTruncated) return;
    const config: TTooltipConfig = {
      content: this.elementRef.nativeElement.textContent,
      title: null,
      element: this.elementRef.nativeElement,
      hasDistance: false,
      xAxis: 'end',
      yAxis: 'end',
    };
    this.tooltip = createTooltip(config);
    if (!this.tooltip) return;
    this.anchorWatcher = watchTooltipAnchor(this.elementRef.nativeElement, {
      onHidden: this.removeTooltip,
      onMove: () => {
        if (this.tooltip) setTooltipPosition(this.tooltip, config);
      },
    });
  };

  private observeVibility(): void {
    // Sem o disconnect o observer anterior fica órfão a cada re-setup e só o
    // último é limpo no destroy.
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = new this.anchorWindow.IntersectionObserver(entries => {
      if (!entries[0].isIntersecting) this.removeTooltip();
    });

    this.intersectionObserver.observe(this.elementRef.nativeElement);
  }
}